// src/components/BookingSuccessMessage.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../src/config";
import { ClipLoader } from "react-spinners";

const BookingSuccessMessage = ({ booking = {}, token }) => {
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);

  // booking_code may come from parent props (if available)
  const booking_code = booking?.booking_code;

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!booking_code) {
        setLoading(false);
        return;
      }

      try {
        const accesstoken = localStorage.getItem("accessToken") || "";
        const apiUrl = accesstoken
          ? `${API_BASE_URL}bookings/combo-details`       //. logged-in user route
          : `${API_BASE_URL}bookings/combo-details/guest`; //. guest route

        const headers = {
          "Content-Type": "application/json",
        };

        // Add token only if available
        if (accesstoken) {
          headers.Authorization = `Bearer ${accesstoken}`;
        }

        const response = await axios.post(
          apiUrl,
          { booking_id: booking_code },
          { headers }
        );

        if (response.data?.success && response.data?.data?.booking) {
          const confirmedBooking = response.data.data.booking;
          setBookingData(confirmedBooking);

          // This endpoint returns success:true with a real booking object
          // even when the booking was never actually paid for (status still
          // "pending", or "expired" if the hold lapsed) — found via a real
          // unpaid test booking (GL-4629) returning a full success response.
          // Firing Purchase on "the fetch succeeded" alone would record a
          // false conversion for every abandoned/unpaid checkout. payment
          // itself isn't exposed in this response, only status, so this
          // excludes the two states that mean "never actually paid" rather
          // than allowlisting (status pending/expired) — confirmed happens
          // the moment payment succeeds, and every later lifecycle state
          // (checked_in, checked_out, no_show, cancelled) can only be
          // reached from there, so all of them correctly imply a real
          // purchase occurred at some point.
          const reallyPaid = confirmedBooking.status !== "pending" && confirmedBooking.status !== "expired";

          // Dedupe against real data, not just this component's own
          // lifecycle — this effect re-runs if `token` changes (e.g. a
          // guest signs up right on this confirmation page), which would
          // otherwise re-fire Purchase a second time for the same booking.
          // sessionStorage (not a ref) survives that remount/re-fetch.
          if (reallyPaid && typeof window.fbq === "function") {
            const dedupeKey = `meta_purchase_fired_${booking_code}`;
            if (!sessionStorage.getItem(dedupeKey)) {
              const bookingSummary = confirmedBooking.summary || {};
              window.fbq("track", "Purchase", {
                value: Number(bookingSummary.total_price || bookingSummary.total_amount || 0),
                currency: "INR",
                content_ids: [confirmedBooking.booking_code || booking_code],
              });
              sessionStorage.setItem(dedupeKey, "1");
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch booking details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [booking_code, token]);

  // Loading state
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <h3><ClipLoader loading={loading} size={30} /></h3>
      </div>
    );
  }

  // Fallback if no data
  if (!bookingData) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <h2>No booking details available.</h2>
        <p>Please start your booking again.</p>
      </div>
    );
  }

  //. Updated destructuring (with booking_code included)
  const {
    booking_code: code,
    stay,
    rooms = [],
    summary = {},
    guests = [],
    user,
  } = bookingData;

  const check_in = summary?.check_in;
  const check_out = summary?.check_out;
  const guest_count =
    summary?.guest_count || (summary?.adults || 0) + (summary?.children || 0);
  const room_count = summary?.room_count || 0;
  const nights = summary?.nights || 0;
  const total_price = summary?.total_price || summary?.total_amount || 0;
  const isValidText = (value) => {
    if (value === null || value === undefined) return false;

    const str = String(value).trim().toLowerCase();

    return str !== "" && str !== "0" && str !== "na" && str !== "null";
  };
  return (
    <section className="profilecontainer">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-9">
            <div className="profilerightouter" style={{ padding: "20px" }}>
              {/* Header */}
              <div
                style={{
                  textAlign: "center",
                  color: "#fff",
                  borderRadius: "8px 8px 0 0",
                  marginBottom: "20px",
                }}
              >
                <h3 style={{ margin: 0 }}>Booking Confirmed!</h3>
                <p style={{ margin: "5px 0 0" }}>
                  Your booking was successful.
                </p>
                {code && (
                  <p style={{ margin: "10px 0 0", fontWeight: "bold" }}>
                    Booking Code: {code}
                  </p>
                )}
              </div>

              {/* Room Image */}
              {/* {room?.images?.length > 0 && room?.images[0]?.url && (
                <div style={{ marginBottom: "20px", textAlign: "center" }}>
                  <img
                    src={room.images[0].url}
                    alt={room?.title || "Room image"}
                    style={{
                      width: "100%",
                      maxHeight: "250px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                </div>
              )} */}

              {stay?.cover_image && (
                <div className="row gx-0 mb-3 mb-md-5">
                  <div className="col-12 mb-2">
                    <div className="bookingimg">
                      <img
                        src={stay.cover_image}
                        alt={stay?.title}
                        style={{
                          width: "600px",
                          height: "380px",
                          objectFit: "cover",
                          borderRadius: "10px"
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}


              {/* Booking Info */}
              <h4 style={{ marginBottom: "10px", color: "#007bff" }}>
                {stay?.title || "--"} - {rooms.map(r => r.title).join(", ")}
              </h4>
              <p>{stay?.address || "--"}</p>

              {rooms && rooms.length > 0 && (
                <>
                  <h3>Room Details</h3>

                  {rooms.map((room, index) => (
                    <div
                      key={room.id || index}
                      className="mb-4 pb-3"
                      style={{ borderBottom: "1px solid #ddd" }}
                    >
                      <p><strong>Room Name:</strong> {room.title}</p>

                      {isValidText(room.description) && (
                        <p><strong>Description:</strong> {room.description}</p>
                      )}

                      <p>
                        <strong>Price per Night:</strong> ₹{room.price_per_night}
                      </p>

                      <p>
                        <strong>Room Count:</strong> {room.room_count}
                      </p>

                      {/*. Image BELOW */}
                      {room.cover_image?.length > 0 && (
                        <div className="mt-2">
                          <img
                            src={room.cover_image}
                            alt={room.title}
                            style={{
                              width: "240px",
                              height: "160px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "1px solid #ccc"
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  margin: "10px 0",
                }}
              >
                <span>
                  <strong>Check-in:</strong>{" "}
                  {check_in ? new Date(check_in).toLocaleDateString("en-GB", { timeZone: "UTC" }) : "--"}
                </span>
                <span>
                  <strong>Check-out:</strong>{" "}
                  {check_out ? new Date(check_out).toLocaleDateString("en-GB", { timeZone: "UTC" }) : "--"}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "15px",
                }}
              >
                <span>
                  <strong>Guests:</strong> {guest_count}
                </span>
                <span>
                  <strong>Rooms:</strong> {room_count}
                </span>
              </div>

              {/* Price Summary */}
              <div className="col-12">
                <div className="pricertbx">
                  <h3>Price Breakup</h3>

                  {/* Room x Nights Breakdown */}
                  {(() => {
                    const nights = summary?.nights || 1;
                    const roomCount = summary?.room_count || 1;
                    const basePrice = parseFloat(summary?.base_price || 0);
                    const totalRoomPriceWithoutTax = basePrice;

                    return (
                      <div className="d-flex gx-0 justify-content-between priceborder pb-2 pt-2">
                        <span>
                          <p>
                            <strong>Room {roomCount}:</strong>{" "}
                            <span className="bestprice">
                              ₹{(basePrice / nights).toFixed(2)} per night × {nights} night{nights > 1 ? "s" : ""}
                            </span>
                          </p>
                        </span>

                        <span className="p-0 prtvalue text-end">
                          ₹{totalRoomPriceWithoutTax}
                        </span>
                      </div>
                    );
                  })()}

                  {/* GST */}
                  <div className="d-flex gx-0 justify-content-between priceborder pb-2 pt-2">
                    <p className="mb-0">Total GST</p>
                    <span className="p-0 prtvalue text-end">
                      ₹{summary?.room_gst_amount || 0}
                    </span>
                  </div>

                  {/* GST Split (CGST / SGST) */}
                  <div className="priceborder pb-2 pt-2 ps-3">
                    <div className="d-flex justify-content-between">
                      <p className="mb-1">CGST</p>
                      <span className="prtvalue">₹{(summary?.room_gst_amount / 2) || 0}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <p className="mb-1">SGST</p>
                      <span className="prtvalue">₹{(summary?.room_gst_amount / 2) || 0}</span>
                    </div>
                  </div>

                  {/* Commission (if any) */}
                  {summary?.commission_amount && parseFloat(summary.commission_amount) > 0 && (
                    <>
                      <div className="d-flex gx-0 justify-content-between priceborder pb-2 pt-2">
                        <p className="mb-0">Platform Service Fee</p>
                        <span className="p-0 prtvalue text-end">
                          ₹{summary?.commission_amount}
                        </span>
                      </div>

                      {/* Commission GST Split */}
                      <div className="priceborder pb-2 pt-2 ps-3">
                        <div className="d-flex justify-content-between">
                          <p className="mb-1">CGST on Platform Fee</p>
                          <span className="prtvalue">₹{(summary?.commission_gst_amount / 2) || 0}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <p className="mb-1">SGST on Platform Fee</p>
                          <span className="prtvalue">₹{(summary?.commission_gst_amount / 2) || 0}</span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Total Paid Box (Your requested format) */}
                  <div
                    style={{
                      backgroundColor: "#e6f7ff",
                      padding: "15px",
                      borderRadius: "10px",
                      textAlign: "center",
                      marginBottom: "20px",
                      marginTop: "20px",
                    }}
                  >
                    <h3 style={{ margin: 0 }}>
                      Total: ₹{total_price ? parseFloat(total_price).toLocaleString() : 0}
                    </h3>
                    <small style={{ color: "#555" }}>(Includes all taxes and fees)</small>
                  </div>
                </div>
              </div>


              {/* Guest Details */}
              {guests.length > 0 ? (
                <div style={{ marginBottom: "20px" }}>
                  <h5>Guest Details</h5>
                  <ul>
                    {guests.map((g, index) => (
                      <li key={index}>
                        {g.first_name} {g.last_name}
                        {g.children_age && (
                          <small> (Child age: {g.children_age})</small>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                user && (
                  <div style={{ marginBottom: "20px" }}>
                    <h5>Guest Details</h5>
                    <ul>
                      <li>
                        <strong>Name:</strong> {user.firstname} {user.lastname}
                      </li>
                      <li>
                        <strong>Email:</strong> {user.email}
                      </li>
                      <li>
                        <strong>Phone:</strong> {user.phone}
                      </li>
                    </ul>
                  </div>
                )
              )}
              <div style={{ textAlign: "center", marginTop: "20px" }}>
                {localStorage.getItem("accessToken") ? (
                  <>
                    {code ? (
                      <Link
                        to={`/booking-details/${code}`}
                        className="theme-btn"
                        style={{ textDecoration: "none" }}
                      >
                        View Booking Details
                      </Link>
                    ) : (
                      <Link
                        to="/mybookings"
                        className="theme-btn"
                        style={{ textDecoration: "none" }}
                      >
                        Go to My Bookings
                      </Link>
                    )}

                    <div style={{ marginTop: "15px" }}>
                      <Link to="/" style={{ color: "#007bff" }}>
                        ← Back to Home
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <p
                      style={{
                        marginTop: "10px",
                        color: "#333",
                        fontSize: "16px",
                        lineHeight: "1.6",
                      }}
                    >
                      Thank you for your booking! A confirmation email with all your booking
                      details has been sent to{" "}
                      <strong>
                        {bookingData?.guests?.[0]?.email || "your registered email address"}
                      </strong>
                      . Please check your inbox and spam folder for details.
                    </p>
                    <div style={{ marginTop: "15px" }}>
                      <Link to="/" style={{ color: "#007bff" }}>
                        ← Back to Home
                      </Link>
                    </div>
                  </>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default BookingSuccessMessage;
